import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { PasswordInput } from '@alascorporation/ui'
import { api, storeSession } from '../lib/api'
import { AuthShell } from '../components/AuthShell'

function getApiErrorMessage(error: unknown, fallback: string): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
  ) {
    return (error as { response: { data: { error: string } } }).response.data.error
  }
  return fallback
}

export function RegisterPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const hasSsoRequest = searchParams.has('client_id') && searchParams.has('redirect_uri')

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    // The submit button disables while isLoading, but that only blocks a
    // mouse click on it — pressing Enter inside the form calls this handler
    // directly and would otherwise fire a second concurrent request.
    if (isLoading) return
    setError('')
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.')
      return
    }
    setIsLoading(true)
    try {
      const { data } = await api.post('/auth/register', { email, password, name })
      storeSession(data.data.user, data.data.token)
      navigate(hasSsoRequest ? `/authorize?${searchParams.toString()}` : '/', { replace: true })
    } catch (err) {
      setError(getApiErrorMessage(err, 'No se pudo crear la cuenta'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthShell>
      <div className="auth-card">
        <p className="auth-eyebrow">SateLab</p>
        <h1 className="auth-title">Crear cuenta</h1>
        <p className="auth-subtitle">
          Una sola cuenta para acceder a todos los servicios del ecosistema.
        </p>

        {error ? (
          <div className="auth-error" role="alert">
            {error}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="auth-form">
          <label className="auth-label" htmlFor="name">
            Nombre
          </label>
          <input
            id="name"
            type="text"
            required
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tu nombre"
            className="auth-input"
          />

          <label className="auth-label" htmlFor="email">
            Correo
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com"
            className="auth-input"
          />

          <label className="auth-label" htmlFor="password">
            Contraseña
          </label>
          <PasswordInput
            id="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mínimo 8 caracteres"
            className="auth-input"
          />

          <button type="submit" className="auth-button" disabled={isLoading}>
            {isLoading ? 'Creando cuenta...' : 'Crear cuenta'}
          </button>
        </form>

        <p className="auth-footer">
          Ya tienes cuenta? <Link to={`/login?${searchParams.toString()}`}>Iniciar sesión</Link>
        </p>
      </div>
    </AuthShell>
  )
}
